<template>
  <div class="group/slider overflow-hidden tablet:px-section-x">
    <div class="tablet:container">
      <div
        v-if="items.length > 3"
        ref="arrowsEl"
        class="flicking-arrows hidden desktop:block"
      >
        <button
          class="flicking-arrow-prev absolute right-full top-1/2 z-50 mr-8 -translate-y-1/2 rounded-full bg-background p-3 opacity-0 transition group-hover/slider:opacity-100"
        >
          <SpriteSymbol name="chevron-left" class="size-size-4" />
        </button>
        <button
          class="flicking-arrow-next absolute left-full top-1/2 z-50 ml-8 -translate-y-1/2 rounded-full bg-background p-3 opacity-0 transition group-hover/slider:opacity-100"
        >
          <SpriteSymbol name="chevron-right" class="size-size-4" />
        </button>
      </div>
      <div ref="flickingEl" class="flicking-viewport !overflow-visible">
        <div
          class="flicking-camera teaser-slider overflow-auto tablet:overflow-visible tablet:pl-0"
        >
          <TeaserThird
            v-for="(item, index) in itemsMapped"
            :key="index"
            v-bind="item.teaserProps"
            class="teaser-slider-item"
            :class="{
              'tablet:opacity-30': index > 2,
            }"
          >
            <template v-if="item.isZurichCard" #media>
              <ZurichCardImage :id="item.uuid" />
            </template>
          </TeaserThird>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Plugin } from '@egjs/flicking'
import type { TeaserProps } from '../Teaser/types'
import type { FieldListItem } from '#blokkli/types'
import { notNullish } from '@vueuse/core'
import { buildTeaserProps } from '../Paragraph/Teaser/helper'
import { getParagraphOfBundle } from '~/helpers/paragraphs'
import { Arrow } from '@egjs/flicking-plugins'
import Fade from '~/helpers/flicking/Fade'

const props = defineProps<{
  id: string
  items: TeaserProps[] | FieldListItem[]
  isEditing?: boolean
}>()

const { isMobile } = useViewport()

const flickingEl = ref<HTMLDivElement>()
const arrowsEl = ref<HTMLDivElement>()

type MappedItem = {
  teaserProps: TeaserProps
  isZurichCard: boolean
  uuid: string
}
const itemsMapped = computed<MappedItem[]>(() => {
  return props.items
    .map((v, index) => {
      if ('uuid' in v && 'bundle' in v) {
        const isZurichCard = v.options?.teaserMedia === 'zurichCard'
        const showOpeningHours = v.options?.showOpeningHours !== '0'
        const teaserParagraph = getParagraphOfBundle(v, 'teaser')
        if (teaserParagraph) {
          const teaserProps = buildTeaserProps(teaserParagraph)
          if (teaserProps) {
            return {
              teaserProps: {
                ...teaserProps,
                showOpeningHours,
              },
              isZurichCard,
              uuid: v.uuid,
            }
          }
        }

        return null
      }

      return {
        teaserProps: v,
        isZurichCard: false,
        uuid: props.id + '-' + index.toString(),
      }
    })
    .filter(notNullish)
})

const isEnabled = computed(
  () => !isMobile.value && !props.isEditing && itemsMapped.value.length > 3,
)
const persistKey = computed(() => props.id)

await useFlicking(flickingEl, {
  flickingOptions: {
    panelsPerView: 3,
    align: 'prev',
    bound: true,
  },
  isEnabled,
  persistKey,
  plugins: () => {
    const plugins: Plugin[] = [new Fade()]
    if (arrowsEl.value) {
      plugins.push(
        new Arrow({
          parentEl: arrowsEl.value,
        }),
      )
    }

    return plugins
  },
})
</script>

<style lang="postcss">
.teaser-slider {
  padding-left: calc(var(--container-spacer));
  padding-right: calc(var(--container-spacer));

  @screen tablet {
    @apply px-0;
  }
}

.teaser-slider-item {
  --teaser-slider-width: 200px;
  --teaser-slider-gap: 16px;
  margin-right: var(--teaser-slider-gap);
  width: var(--teaser-slider-width);

  @apply last:mr-0;

  @screen tablet {
    --teaser-slider-width: calc((100% - var(--teaser-slider-gap) * 2) / 3);
  }
}
</style>
